// API Configuration
// Dynamic API base URL detection
const getApiBaseUrl = () => {
  // If explicitly set via environment variable, use it
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL
  }

  // Development mode - use localhost
  if (import.meta.env.DEV) {
    return 'http://localhost:8000'
  }

  // Production mode - use current domain (works with any ingress IP)
  if (typeof window !== 'undefined') {
    return `${window.location.protocol}//${window.location.host}`
  }

  // Fallback for SSR or build time
  return ''
}

export const API_BASE_URL = getApiBaseUrl()
export const API_PREFIX = '/api/v1'

// Authentication
export const TOKEN_KEY = 'sr_internal_token'
export const USER_KEY = 'sr_internal_user'
export const REFRESH_TOKEN_KEY = 'sr_internal_refresh_token'

// Routes
export const ROUTES = {
  HOME: '/',
  LOGIN: '/login',
  REGISTER: '/register',
  DASHBOARD: '/dashboard',
  PROFILE: '/profile',
  EDIT_PROFILE: '/profile/edit',
  
  // Brands
  BRANDS: '/brands',
  BRAND_DETAIL: '/brands/:id',
  BRAND_CREATE: '/brands/create',
  BRAND_EDIT: '/brands/:id/edit',
  
  // Data Inputs
  DATA_INPUTS: '/data-inputs',
  DATA_INPUT_DETAIL: '/data-inputs/:id',
  DATA_INPUT_CREATE: '/data-inputs/create',
  DATA_INPUT_EDIT: '/data-inputs/:id/edit',
  // Data Input Attributes
  ATTRIBUTE_DETAIL: '/data-inputs/:dataInputId/attributes/:attributeId',
  ATTRIBUTE_CREATE: '/data-inputs/:dataInputId/attributes/create',
  ATTRIBUTE_EDIT: '/data-inputs/:dataInputId/attributes/:attributeId/edit',

  // Data Input Records
  RECORD_CREATE: '/data-inputs/:dataInputId/records/create',
  RECORD_EDIT: '/data-inputs/:dataInputId/records/:recordId/edit',
  RECORD_BULK_UPLOAD: '/data-inputs/:dataInputId/records/bulk-upload',
  
  // Areas
  AREAS: '/areas',
  AREA_DETAIL: '/areas/:id',
  AREA_CREATE: '/areas/create',
  AREA_EDIT: '/areas/:id/edit',

  // Users (Admin and Manager)
  USERS: '/users',
  USER_DETAIL: '/users/:id',
  USER_CREATE: '/users/create',
  USER_EDIT: '/users/:id/edit',
  
  // Settings
  SETTINGS: '/settings',

  // Audit Logs
  AUDIT_LOGS: '/audit-logs',

  // Developer (Admin only)
  DEVELOPER_PERMISSIONS: '/developer/permissions',
}

// User Roles
export const USER_ROLES = {
  ADMIN: 'admin',
  MANAGER: 'manager',
  AREA_MANAGER: 'area_manager',
  USER: 'user',
}

// Permissions
export const PERMISSIONS = {
  VIEW: 'view',
  CREATE: 'create',
  EDIT: 'edit',
  DELETE: 'delete',
  MANAGE_USERS: 'manage_users',
  APPROVE_REQUESTS: 'approve_requests',
}

// Data Types
export const DATA_TYPES = {
  TEXT: 'Text',
  NUMBER: 'Number',
  DECIMAL: 'Decimal',
  BOOLEAN: 'Boolean',
  DATE: 'Date',
  DATETIME: 'Datetime',
  EMAIL: 'Email',
  URL: 'URL',
  PHONE_NUMBER: 'Phone Number',
  FILE: 'File',
  IMAGE: 'Image',
  VIDEO: 'Video',
  AUDIO: 'Audio',
  DOCUMENT: 'Document',
  ARCHIVE: 'Archive',
}

// Validation Rules
export const VALIDATION_RULES = {
  REQUIRED: 'required',
  UNIQUE: 'unique',
  MIN_LENGTH: 'min_length',
  MAX_LENGTH: 'max_length',
  MIN_VALUE: 'min_value',
  MAX_VALUE: 'max_value',
  PATTERN: 'pattern',
  ALLOWED_VALUES: 'allowed_values',
  EMAIL: 'email',
  URL: 'url',
  DATE_FORMAT: 'date_format',
  FILE_EXTENSION: 'file_extension',
  FILE_SIZE: 'file_size',
}

// UI Constants
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 20,
  PAGE_SIZE_OPTIONS: [10, 20, 50, 100],
}

export const TOAST_MESSAGES = {
  // Authentication
  LOGIN_SUCCESS: 'Welcome back!',
  LOGIN_ERROR: 'The email or password you entered is incorrect. Please try again.',
  LOGOUT_SUCCESS: 'You have been logged out successfully.',
  SESSION_EXPIRED: 'Your session has expired. Please log in again.',

  // General actions
  SAVE_SUCCESS: 'Changes saved successfully!',
  CREATE_SUCCESS: 'Created successfully!',
  UPDATE_SUCCESS: 'Updated successfully!',
  DELETE_SUCCESS: 'Deleted successfully!',

  // Errors
  ERROR_GENERIC: 'Something went wrong. Please try again.',
  NETWORK_ERROR: 'Unable to connect to the server. Please check your internet connection and try again.',
  UNAUTHORIZED: 'You don\'t have permission to perform this action.',
  VALIDATION_ERROR: 'Please check your input and try again.',
  NOT_FOUND: 'The requested item could not be found.',
  SERVER_ERROR: 'A system error occurred. Please try again later.',

  // Loading states
  LOADING: 'Loading...',
  PROCESSING: 'Processing...',
  UPLOADING: 'Uploading...',
}

// Status Constants
export const STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error',
}

// Theme
export const THEME = {
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system',
}